import React from 'react';
import styled from '@emotion/styled';
import { css } from '@emotion/react';

import { Icon } from '../icons/Icon';
import { withSpacingStyles } from '../withSpacingStyles';

type Props = {
  /**
   * 	Specifies that the drop-down list should automatically get focus when the page loads.
   */
  autoFocus?: boolean,
  /**
   * 	Specifies that a drop-down list should be disabled
   */
  disabled?: boolean,
  /**
   * 	Defines one or more forms the select field belongs to
   */
  form?: string,
  /**
   * 	Specifies that multiple options can be selected at once
   */
  multiple?: boolean,
  /**
   * 	Specifies that the user is required to select a value before submitting the form
   */
  required?: boolean,
  /**
   * 	Defines the number of visible options in a drop-down list
   */
  size?: number,
  /**
   * 	Defines a name for the drop-down list
   */
  name: string,
  /**
   * 	Specifies a unique id for an HTML element
   */
  id?: string,
  /**
   * 	Specifies the tab order of an element (when the "tab" button is used for navigating)
   */
  tabIndex: number,
  /**
   * 	Specifies the name of icon on the left side
   */
  iconName?: string,
  fontSize?: string,
  children: React.Node,
};

export const Select = withSpacingStyles(({ children, iconName, name, fontSize = 'md', tabIndex = 0, ...rest }: Props) => (
  <Container>
    {iconName && <CustomIcon name={iconName} />}
    <FormSelect data-test={`select-${name}`} name={name} hasIconLeft={iconName} tabIndex={tabIndex} {...rest}>
      {children}
    </FormSelect>
    <ChevronDown name="ChevronDown" />
  </Container>
));

const Container = styled('div')`
  position: relative;
  overflow: hidden;
`;

const FormSelect = styled('select')`
  width: 100%;
  display: block;
  padding: ${({ hasIconLeft }) => (hasIconLeft ? '10px 40px' : '10px 40px 10px 16px')};
  background-color: ${({ theme }) => theme.color.white};
  color: ${({ theme }) => theme.color.grey};
  font-size: ${({ theme, fontSize }) => theme.fontSize[fontSize]};
  border: ${({ theme }) => theme.stroke.default};
  border-radius: ${({ theme }) => theme.borderRadius.medium};
  appearance: none;
  cursor: pointer;
  transition: ${({ theme }) => theme.motion.transition};

  &:focus {
    outline: 0;
    border-color: ${({ theme }) => theme.color.info};
    box-shadow: ${({ theme }) => theme.boxShadow.focus};
  }

  /* Hide arrow icon in IE browsers */
  ::-ms-expand {
    display: none;
  }
`;

const icon = css`
  position: absolute;
  top: 50%;
  transform: translateY(-50%);
  pointer-events: none;
`;

const ChevronDown = styled(Icon)`
  ${icon};
  right: 16px;
  fill: ${({ theme }) => theme.color.grey};
  font-size: ${({ theme }) => theme.fontSize.xs};
`;

const CustomIcon = styled(Icon)`
  ${icon};
  left: 16px;
  fill: ${({ theme }) => theme.color.grey};
  font-size: ${({ theme }) => theme.fontSize.xs};
`;
